import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import PrivacyPolicyModal from '../legal/PrivacyPolicyModal';
import TermsModal from '../legal/TermsModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

interface RegisterFormProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onClose, onSwitchToLogin }) => {
  const { register, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  // Availability checking state
  const [availabilityStatus, setAvailabilityStatus] = useState<Record<string, 'checking' | 'available' | 'taken' | 'error' | undefined>>({});
  const usernameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (usernameTimeoutRef.current) {
        clearTimeout(usernameTimeoutRef.current);
      }
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, []);

  // Real-time validation functions
  const validateUsername = (username: string): string => {
    if (!username.trim()) return '';
    
    if (username.length < 3) {
      return 'Username must be at least 3 characters long';
    }
    if (username.length > 20) {
      return 'Username must be no more than 20 characters long';
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      return 'Username can only contain letters, numbers, underscores, and hyphens';
    }
    return '';
  };

  const validateEmailFormat = (email: string): string => {
    if (!email.trim()) return '';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return '';
    
    const errors = [];
    
    if (password.length < 8) {
      errors.push('at least 8 characters');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('one lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('one uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('one special character');
    }
    
    if (errors.length > 0) {
      return `Password must contain ${errors.join(', ')}`;
    }
    
    return '';
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string => {
    if (!confirmPassword) return '';
    
    if (confirmPassword !== password) {
      return 'Passwords do not match';
    }
    return '';
  };

  // Debounced availability checking functions
  const checkUsernameAvailability = async (username: string) => {
    if (!username.trim() || username.length < 3) return;
    
    try {
      setAvailabilityStatus(prev => ({ ...prev, username: 'checking' }));
      
      const response = await axios.get(`${API_BASE_URL}/auth/check-username?username=${encodeURIComponent(username)}`);
      
      if (response.data.success) {
        setAvailabilityStatus(prev => ({ 
          ...prev, 
          username: response.data.available ? 'available' : 'taken' 
        }));
        
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, username: response.data.message }));
        }
      } else {
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      }
    } catch (error: any) {
      console.error('Username availability check error:', error);
      
      if (error.response?.status === 429) {
        // Rate limited - show helpful message
        setErrors(prev => ({ ...prev, username: 'Please slow down. Try again in a moment.' }));
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      } else {
        setAvailabilityStatus(prev => ({ ...prev, username: 'error' }));
      }
    }
  };

  const checkEmailAvailability = async (email: string) => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    
    try {
      setAvailabilityStatus(prev => ({ ...prev, email: 'checking' }));
      
      const response = await axios.get(`${API_BASE_URL}/auth/check-email?email=${encodeURIComponent(email)}`);
      
      if (response.data.success) {
        setAvailabilityStatus(prev => ({ 
          ...prev, 
          email: response.data.available ? 'available' : 'taken' 
        }));
        
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, email: response.data.message }));
        }
      } else {
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      }
    } catch (error: any) {
      console.error('Email availability check error:', error);
      
      if (error.response?.status === 429) {
        // Rate limited - show helpful message
        setErrors(prev => ({ ...prev, email: 'Please slow down. Try again in a moment.' }));
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      } else {
        setAvailabilityStatus(prev => ({ ...prev, email: 'error' }));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear general error when user starts typing
    if (generalError) {
      setGeneralError('');
    }

    // Real-time validation
    const newErrors = { ...errors };

    switch (name) {
      case 'username':
        newErrors.username = validateUsername(value);
        
        // Clear availability status when user starts typing
        setAvailabilityStatus(prev => ({ ...prev, username: undefined }));
        
        // Debounced availability check
        if (usernameTimeoutRef.current) {
          clearTimeout(usernameTimeoutRef.current);
        }
        
        if (value.trim().length >= 3 && !newErrors.username) {
          usernameTimeoutRef.current = setTimeout(() => {
            checkUsernameAvailability(value.trim());
          }, 800); // 800ms debounce
        }
        break;
        
      case 'email':
        newErrors.email = validateEmailFormat(value);
        
        // Clear availability status when user starts typing
        setAvailabilityStatus(prev => ({ ...prev, email: undefined }));
        
        // Debounced availability check
        if (emailTimeoutRef.current) {
          clearTimeout(emailTimeoutRef.current);
        }
        
        if (value.trim() && !newErrors.email) {
          emailTimeoutRef.current = setTimeout(() => {
            checkEmailAvailability(value.trim());
          }, 800); // 800ms debounce
        }
        break;
        
      case 'password':
        newErrors.password = validatePassword(value);
        // Also validate confirm password if it exists
        if (formData.confirmPassword) {
          newErrors.confirmPassword = validateConfirmPassword(formData.confirmPassword, value);
        }
        break;
        
      case 'confirmPassword':
        newErrors.confirmPassword = validateConfirmPassword(value, formData.password);
        break;
    }

    setErrors(newErrors);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Check for required fields
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    }

    // Add any existing validation errors
    Object.keys(errors).forEach(key => {
      if (errors[key]) {
        newErrors[key] = errors[key];
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log(">>> handleSubmit fired", e);
    e.preventDefault();
    e.stopPropagation();
    try {
      if (!validateForm()) {
        console.log(">>> validation failed");
        return;
      }

      const result = await register(formData.username, formData.email, formData.password);
      console.log(">>> register result:", result);
      if (result.success) {
        console.log(">>> registration success");
        
        // Check if email verification is required
        if (result.emailVerificationRequired) {
          setGeneralError('');
          setErrors({});
          // Show success message about verification email
          alert('Account created successfully! Please check your email and click the verification link to complete your registration.');
        }
        
        onClose();
        return;
      }

      console.log(">>> handling errors");

      const newErrors: Record<string, string> = {};

      if (result.message.toLowerCase().includes('username')) {
        newErrors.username = 'Username already exists';
        setGeneralError('');
        window.alert('Username already exists');
        console.log(">>> setting generalError:", result.message);
        console.log(">>> newErrors:", newErrors);
      } else if (result.message.toLowerCase().includes('email')) {
        newErrors.email = 'Email already exists';
        setGeneralError('');
        window.alert('Email already in use');
      } else {
        setGeneralError(result.message || 'Registration failed. Please try again.');
      }

      setErrors(newErrors);
    }
    catch (err) {
      console.error(">>> unhandled error in handleSubmit:", err);
    }

    console.log(">>> handleSubmit finished");

    // Clear password fields for security
    setFormData(prev => ({
      ...prev,
      password: '',
      confirmPassword: ''
    }));
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Create Account
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
        >
          ×
        </button>
      </div>
      
      <form
      onSubmit={(e) => {
        e.preventDefault(); // stop browser POST
        e.stopPropagation();
        handleSubmit(e);
      }}
      className="space-y-4"
      noValidate>
        {generalError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
          >
            <p className="text-red-600 dark:text-red-400 text-sm">{generalError}</p>
          </motion.div>
        )}
        
        <div className="relative">
          <Input
            name="username"
            type="text"
            label="Username"
            placeholder="Choose a username"
            value={formData.username}
            onChange={handleInputChange}
            error={errors.username}
            disabled={isLoading}
            helperText={
              availabilityStatus.username === 'checking' ? '🔄 Checking availability...' :
              availabilityStatus.username === 'available' ? '✅ Username is available' :
              availabilityStatus.username === 'taken' ? '❌ Username is already taken' :
              '3+ characters, letters, numbers, underscores, and hyphens only'
            }
          />
          {availabilityStatus.username === 'checking' && (
            <div className="absolute right-3 top-8 text-blue-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            </div>
          )}
        </div>
        
        <div className="relative">
          <Input
            name="email"
            type="email"
            label="Email Address"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            disabled={isLoading}
            helperText={
              availabilityStatus.email === 'checking' ? '🔄 Checking availability...' :
              availabilityStatus.email === 'available' ? '✅ Email is available' :
              availabilityStatus.email === 'taken' ? '❌ Email is already in use' :
              undefined
            }
          />
          {availabilityStatus.email === 'checking' && (
            <div className="absolute right-3 top-8 text-blue-500">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
            </div>
          )}
        </div>
        
        <Input
          name="password"
          type="password"
          label="Password"
          placeholder="Create a strong password"
          value={formData.password}
          onChange={handleInputChange}
          error={errors.password}
          disabled={isLoading}
          helperText="8+ characters with uppercase, lowercase, number, and special character"
        />
        
        <Input
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          placeholder="Confirm your password"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={errors.confirmPassword}
          disabled={isLoading}
        />
        
        <Button
          type="submit"
          className="w-full"
          isLoading={isLoading}
          disabled={
            isLoading || 
            !formData.username.trim() || 
            !formData.email.trim() || 
            !formData.password || 
            !formData.confirmPassword ||
            !!errors.username ||
            !!errors.email ||
            !!errors.password ||
            !!errors.confirmPassword ||
            availabilityStatus.username === 'checking' ||
            availabilityStatus.email === 'checking' ||
            availabilityStatus.username === 'taken' ||
            availabilityStatus.email === 'taken'
          }
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
      
      <div className="mt-6 text-xs text-gray-500 dark:text-gray-400 text-center">
        By creating an account, you agree to our{' '}
        <button
          onClick={() => setShowTerms(true)}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 underline"
        >
          Terms & Conditions
        </button>{' '}
        and{' '}
        <button
          onClick={() => setShowPrivacyPolicy(true)}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-400 underline"
        >
          Privacy Policy
        </button>
      </div>

      {/* Legal Modals */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyPolicy} 
        onClose={() => setShowPrivacyPolicy(false)} 
      />
      <TermsModal 
        isOpen={showTerms} 
        onClose={() => setShowTerms(false)} 
      />
    </div>
  );
};

export default RegisterForm;
